import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t, i18n } = useTranslation();

  const languages = [
    { code: 'de', flag: '🇩🇪' },
    { code: 'en', flag: '🇬🇧' },
    { code: 'nl', flag: '🇳🇱' },
    { code: 'fr', flag: '🇫🇷' },
    { code: 'pl', flag: '🇵🇱' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-t-[40px]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-8">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">
          {/* Newsletter - 40% */}
          <div className="lg:col-span-5">
            <img
              src="https://public.readdy.ai/ai/img_res/63843a04-43d0-4a10-a6f1-976e3e4aaad2.png"
              alt="Dutch Fires"
              className="h-10 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-sm text-gray-400 mb-6">{t('common:footer.tagline')}</p>
            
            <form className="mb-4">
              <div className="relative">
                <input
                  type="email"
                  placeholder={t('common:footer.newsletter.placeholder')}
                  className="w-full bg-transparent border-b border-amber-500 pb-3 pr-12 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors"
                />
                <button
                  type="submit"
                  className="absolute right-0 bottom-2 w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center hover:bg-amber-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-black text-lg"></i>
                </button>
              </div>
            </form>
            <p className="text-xs text-gray-500">{t('common:footer.newsletter.disclaimer')}</p>
          </div>

          {/* Products - 20% */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">{t('common:footer.products')}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/category/inbouwhaarden" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  {t('common:nav.builtInFireplaces')}
                </Link>
              </li>
              <li>
                <Link to="/category/3-zijdige-haarden" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  {t('common:nav.threeSidedFireplaces')}
                </Link>
              </li>
              <li>
                <Link to="/category/vrijstaande-haarden" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  {t('common:nav.wallFireplaces')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Information - 20% */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">{t('common:footer.information')}</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/dealer-registration" className="text-sm text-amber-500 font-semibold hover:text-amber-400 hover:underline transition-colors cursor-pointer">
                  {t('common:nav.becomeDealer')}
                </Link>
              </li>
              <li>
                <Link to="/#about" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  {t('common:nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/#inspiration" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  {t('common:nav.inspiration')}
                </Link>
              </li>
              <li>
                <Link to="/#contact" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  {t('common:nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact - 20% */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">{t('common:footer.contact')}</h4>
            <ul className="space-y-3 mb-6">
              <li>
                <a href="mailto:info@dutchfires.de" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  info@dutchfires.de
                </a>
              </li>
            </ul>

            {/* Social Icons */}
            <div className="flex gap-3">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:border-amber-500 hover:bg-amber-500/10 transition-all cursor-pointer"
              >
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:border-amber-500 hover:bg-amber-500/10 transition-all cursor-pointer"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center hover:border-amber-500 hover:bg-amber-500/10 transition-all cursor-pointer"
              >
                <i className="ri-youtube-fill text-lg"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Copyright */}
          <p className="text-xs text-gray-500">{t('common:footer.copyright')}</p>

          {/* Language Switcher */}
          <div className="flex items-center gap-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-lg transition-all cursor-pointer ${
                  i18n.language === lang.code
                    ? 'border-2 border-amber-500 bg-amber-500/10'
                    : 'border border-gray-600 hover:border-gray-400'
                }`}
              >
                {lang.flag}
              </button>
            ))}
          </div>

          {/* Legal Links */}
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <Link to="/" className="hover:text-white hover:underline transition-colors cursor-pointer">
              {t('common:footer.legal.imprint')}
            </Link>
            <span>•</span>
            <Link to="/" className="hover:text-white hover:underline transition-colors cursor-pointer">
              {t('common:footer.legal.privacy')}
            </Link>
            <span>•</span>
            <Link to="/" className="hover:text-white hover:underline transition-colors cursor-pointer">
              {t('common:footer.legal.terms')}
            </Link>
            <span>•</span>
            <Link to="/" className="hover:text-white hover:underline transition-colors cursor-pointer">
              {t('common:footer.legal.cookies')}
            </Link>
            <span>•</span>
            <span className="px-2 py-1 border border-amber-500/50 rounded text-amber-500 text-xs whitespace-nowrap">
              {t('common:footer.businessOnly')}
            </span>
          </div>
        </div>

        {/* Readdy Link */}
        <div className="mt-6 text-center">
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </footer>
  );
}
